/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

import * as React from 'react';

import { Anchor, Toaster as VaiToaster, Notification as VaiNotification } from '@vaisala/rockhopper-components';
import { AppNotification, Notification, NotificationContext, appNotificationConfig } from '../state/notifications';
import { Draft, produce } from 'immer';

import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';

interface NotificationItemProps {
  notification: Notification & { key: string };
  dismiss: VoidFunction;
}

const NotificationItem = React.memo(({ notification, dismiss }: NotificationItemProps) => {
  const { t } = useTranslation();
  return (
    <VaiNotification key={notification.key} type={notification.type} duration={notification.duration} onClose={dismiss} closeIcon={notification.closable}>
      {t(notification.content)}
      {notification.linkTo ? (
        <Anchor to={notification.linkTo} openInNewWindow>
          {notification.linkText}
        </Anchor>
      ) : null}
    </VaiNotification>
  );
});

type NotificationWithKey = Notification & { key: string };

type NotificationState = { notifications: Array<NotificationWithKey> };
type Action =
  | {
      type: 'ADD';
      payload: { notification: NotificationWithKey };
    }
  | { type: 'DISMISS'; payload: { key: string } };

const initialState = {
  notifications: [],
};

function reducer(draft: Draft<NotificationState>, action: Action) {
  switch (action.type) {
    case 'ADD': {
      draft.notifications.push(action.payload.notification);
      return;
    }
    case 'DISMISS': {
      const notifications = draft.notifications.filter((n) => n.key !== action.payload.key);
      if (notifications.length !== draft.notifications.length) {
        draft.notifications = notifications;
      }
      return;
    }
    default:
      return;
  }
}

const immerReducer = produce(reducer);

/**
 * While using context is way to use notifications without any global state management lib (redux)
 * it has the disadvantage of being only callable in React.
 * If you need to be able to notify() outside React, e.g. in middleware,
 * it is better to put this state in Redux.
 */

export const NotificationProvider = React.memo(({ children }: { children: React.ReactNode }) => {
  /**
   * the Toaster component is quite sensitive to re-renders - if it renders unnecessarily it will cause the stack to close and open
   * useReducer (or some state management like Redux) will ensure that only the production of a new notification list will cause a rerender
   */
  const [{ notifications }, dispatch] = React.useReducer(immerReducer, initialState);
  const notificationTimeoutsRef = React.useRef<Map<string, number>>(new Map());

  const notify = React.useCallback(
    (notification: Notification) => {
      const key = nanoid();
      const withKey = { ...notification, key };
      dispatch({ type: 'ADD', payload: { notification: withKey } });
      if (notification.duration) {
        const timeout = window.setTimeout(() => {
          dispatch({ type: 'DISMISS', payload: { key } });
          notificationTimeoutsRef.current.delete(key);
        }, notification.duration * 1000);
        notificationTimeoutsRef.current.set(key, timeout);
      }
    },
    [dispatch]
  );

  // allow for predefined notifications
  const notifyById = React.useCallback(
    (id: AppNotification) => {
      if (appNotificationConfig.has(id)) {
        notify(appNotificationConfig.get(id) as Notification);
      }
    },
    [notify]
  );

  // cleanup timeouts not called
  React.useEffect(() => {
    const timeouts = notificationTimeoutsRef.current;
    return () => {
      if (timeouts.size) {
        for (const timeout of Array.from(timeouts.values())) {
          clearTimeout(timeout);
        }
      }
    };
  }, []);

  // make sure this is rendered high enough in the component tree so that the notify methods are available in context
  return (
    <>
      <VaiToaster>
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.key}
            notification={notification}
            dismiss={() => dispatch({ type: 'DISMISS', payload: { key: notification.key } })}
          />
        ))}
      </VaiToaster>
      <NotificationContext.Provider value={{ notify, notifyById }}>{children}</NotificationContext.Provider>
    </>
  );
});
